import { Capacitor } from "@capacitor/core";
import { App, type URLOpenListenerEvent } from "@capacitor/app";

/**
 * Routes voice-launched intents (Siri / Google Assistant) to the React
 * router. Both platforms hand the web app a deep link via
 * `App.addListener('appUrlOpen', …)`:
 *
 *   - iOS: the App Intent (record_encounter) opens
 *     `maie://quick-record` via UIApplication.shared.open(...).
 *   - Android: the App Action target activity calls
 *     `intent.setData(Uri.parse("maie://quick-record"))` before
 *     bouncing to MainActivity.
 *
 * The web app receives the URL on the *running* WebView. We strip the
 * scheme + host and feed the path to the supplied navigate() callback.
 */
const VOICE_INTENT_HOSTS = new Set([
  "quick-record",
  "encounter",
]);

export interface VoiceIntentBridge {
  /** Tear-down — call from the unmount of whoever wired the bridge. */
  dispose: () => void;
}

export async function installVoiceIntentBridge(
  navigate: (path: string) => void,
): Promise<VoiceIntentBridge> {
  if (!Capacitor.isNativePlatform()) {
    return { dispose: () => {} };
  }

  const handler = async (event: URLOpenListenerEvent) => {
    const path = pathFromIntentUrl(event.url);
    if (!path) return;
    navigate(path);
  };

  const sub = await App.addListener("appUrlOpen", handler);

  // Also handle the cold-launch case where the app was *opened* by the
  // intent. getLaunchUrl returns the URL the OS handed us at start.
  try {
    const launch = await App.getLaunchUrl();
    if (launch?.url) {
      const path = pathFromIntentUrl(launch.url);
      if (path) navigate(path);
    }
  } catch {
    /* getLaunchUrl is best-effort */
  }

  return {
    dispose: () => {
      sub.remove();
    },
  };
}

/** Map `maie://quick-record` → `/quick-record`. Unknown hosts → null. */
function pathFromIntentUrl(raw: string): string | null {
  try {
    const url = new URL(raw);
    // maie://quick-record  → host='quick-record', pathname='/'
    // maie://encounter/new → host='encounter',    pathname='/new'
    if (url.protocol !== "maie:" && url.protocol !== "https:") return null;
    if (
      url.protocol === "https:" &&
      url.hostname !== "app.maie.to" &&
      !url.hostname.endsWith(".app.maie.to")
    ) {
      return null;
    }

    if (url.protocol === "maie:") {
      const host = url.hostname || url.pathname.replace(/^\/+/, "").split("/")[0];
      if (!VOICE_INTENT_HOSTS.has(host)) return null;
      const tail = url.pathname && url.pathname !== "/" ? url.pathname : "";
      return `/${host}${tail}`;
    }

    // Universal-link path on app.maie.to
    return url.pathname || "/";
  } catch {
    return null;
  }
}
